import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { 
  ChevronLeft, 
  Lock, 
  CheckCircle, 
  Play, 
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PortalModuleDetail = () => {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [completing, setCompleting] = useState(null);

  const fetchModule = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/classroom/modules/${moduleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch module');
      }
      
      const data = await response.json();
      setModule(data);
    } catch (err) {
      console.error('Error fetching module:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  const handleLessonClick = (lesson) => {
    if (lesson.is_locked) return;
    setExpandedLesson(expandedLesson === lesson.lesson_id ? null : lesson.lesson_id);
  };

  const handleMarkComplete = async (lessonId) => {
    setCompleting(lessonId);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/classroom/progress/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          module_id: moduleId,
          lesson_id: lessonId,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark lesson complete');
      }
      
      const data = await response.json();
      
      // Update local state without full page reload
      if (!data.already_completed) {
        setModule(prev => ({
          ...prev,
          progress_percent: data.progress_percent,
          completed_count: data.completed_count,
          lessons: prev.lessons.map(lesson => 
            lesson.lesson_id === lessonId 
              ? { ...lesson, is_completed: true }
              : lesson
          ),
        }));
      }
    } catch (err) {
      console.error('Error marking complete:', err);
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <PortalLayout activeTab="classroom">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  if (error || !module) {
    return (
      <PortalLayout activeTab="classroom">
        <div className="p-6 lg:p-8 bg-[#F9F6EF] min-h-screen">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm">
              {error || 'Module not found'}
            </div>
            <Link to="/portal/classroom" className="text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mt-4 inline-block transition">
              ← Back to Classroom
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="classroom">
      <div className="p-4 sm:p-6 lg:p-8 bg-[#F9F6EF] min-h-screen">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/portal/classroom" 
            className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Classroom
          </Link>

          {/* Module Header */}
          <div className="mb-8">
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
              The Move · Classroom
            </p>
            <h1 className="font-display italic text-3xl lg:text-4xl text-[#0A1F2E] mb-3 leading-[1.1]">{module.title}</h1>
            <p className="font-body text-[#0A1F2E]/70 mb-4">{module.description}</p>
            
            {/* Progress Bar */}
            <div className="bg-white border border-[#EFE6CF] rounded-2xl p-4 shadow-sm" data-testid="module-progress-bar">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-[#0A1F2E]/60 font-mono text-xs uppercase tracking-[0.12em]">
                  {module.completed_count} of {module.total_lessons} lessons completed
                </span>
                <span className="text-[#2CA8A0] font-mono font-medium" data-testid="module-progress-percent">{module.progress_percent}%</span>
              </div>
              <div className="h-2 bg-[#F4ECD8] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#2CA8A0] rounded-full transition-all duration-500"
                  style={{ width: `${module.progress_percent}%` }}
                />
              </div>
            </div>
          </div>

          {/* Lessons List */}
          <div className="space-y-3">
            {module.lessons.map((lesson, index) => (
              <div 
                key={lesson.lesson_id}
                className={`bg-white border rounded-xl overflow-hidden transition ${
                  expandedLesson === lesson.lesson_id 
                    ? 'border-[#2CA8A0]/60 shadow-sm' 
                    : 'border-[#EFE6CF] hover:border-[#1A5C6E]/40'
                }`}
                data-testid={`lesson-${lesson.lesson_id}`}
              >
                {/* Lesson Row */}
                <button
                  onClick={() => handleLessonClick(lesson)}
                  disabled={lesson.is_locked}
                  className={`w-full p-4 flex items-center justify-between text-left ${
                    lesson.is_locked 
                      ? 'cursor-not-allowed opacity-60' 
                      : 'hover:bg-[#F9F6EF] cursor-pointer'
                  } transition`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Lesson Thumbnail (16:9). Falls back to the cream
                        Play-icon placeholder when no thumbnail is uploaded. */}
                    <div
                      className="relative w-24 h-[54px] sm:w-28 sm:h-16 flex-shrink-0 bg-[#F4ECD8] rounded-md overflow-hidden border border-[#EFE6CF]"
                      data-testid={`lesson-card-thumb-${lesson.lesson_id}`}
                    >
                      {lesson.thumbnail_url ? (
                        <img
                          src={lesson.thumbnail_url}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-5 h-5 text-[#1A5C6E]/40" />
                        </div>
                      )}
                    </div>

                    {/* Lesson Number */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                      lesson.is_completed
                        ? 'bg-[#2CA8A0] text-white'
                        : lesson.is_locked
                        ? 'bg-[#EFE6CF] text-[#0A1F2E]/40'
                        : 'bg-[#F4ECD8] text-[#0A1F2E]'
                    }`}>
                      {lesson.is_completed ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        lesson.order
                      )}
                    </div>
                    
                    {/* Title */}
                    <span className={`font-display italic text-lg truncate ${
                      lesson.is_locked ? 'text-[#0A1F2E]/40' : 'text-[#0A1F2E]'
                    }`}>
                      {lesson.title}
                    </span>
                  </div>
                  
                  {/* Right side */}
                  <div className="flex items-center gap-2 flex-shrink-0 pl-3">
                    {lesson.is_locked ? (
                      <Lock className="w-4 h-4 text-[#0A1F2E]/40" />
                    ) : expandedLesson === lesson.lesson_id ? (
                      <ChevronUp className="w-4 h-4 text-[#1A5C6E]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#1A5C6E]" />
                    )}
                  </div>
                </button>

                {/* Locked Upgrade CTA */}
                {lesson.is_locked && (
                  <div className="px-4 pb-4">
                    <Link
                      to="/portal/upgrade"
                      className="inline-flex items-center gap-2 text-[#1A5C6E] text-sm hover:text-[#0A1F2E] transition font-mono uppercase tracking-[0.12em] text-[11px]"
                    >
                      <Sparkles className="w-4 h-4" />
                      Unlock full access - upgrade now
                    </Link>
                  </div>
                )}

                {/* Expanded Lesson Content */}
                {expandedLesson === lesson.lesson_id && !lesson.is_locked && (
                  <div className="px-4 pb-4 border-t border-[#EFE6CF] mt-2 pt-4">
                    {/* Video Player - uses per-lesson thumbnail as poster
                        when available, falls back to module hero. */}
                    <div className="aspect-video bg-[#F4ECD8] mb-4 relative overflow-hidden rounded-xl">
                      {lesson.cloudinary_video_url ? (
                        <video
                          controls
                          className="w-full h-full"
                          poster={lesson.thumbnail_url || module.thumbnail_url}
                          data-testid={`lesson-video-${lesson.lesson_id}`}
                        >
                          <source src={lesson.cloudinary_video_url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : lesson.thumbnail_url ? (
                        <img
                          src={lesson.thumbnail_url}
                          alt={`${lesson.title} thumbnail`}
                          className="absolute inset-0 w-full h-full object-cover"
                          data-testid={`lesson-thumbnail-${lesson.lesson_id}`}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <Play className="w-12 h-12 text-[#1A5C6E]/40 mx-auto mb-2" />
                            <p className="text-[#0A1F2E]/50 text-sm">Video coming soon</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-[#0A1F2E]/70 text-sm mb-4">{lesson.description}</p>

                    {/* Mark Complete Button */}
                    {!lesson.is_completed ? (
                      <button
                        onClick={() => handleMarkComplete(lesson.lesson_id)}
                        disabled={completing === lesson.lesson_id}
                        className="bg-[#0A1F2E] text-white px-5 py-2.5 text-sm font-medium rounded-full hover:bg-[#163448] transition disabled:opacity-50 flex items-center gap-2"
                        data-testid={`mark-complete-btn-${lesson.lesson_id}`}
                      >
                        {completing === lesson.lesson_id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Mark Complete
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-[#2CA8A0] text-sm font-mono uppercase tracking-[0.12em] text-[11px]">
                        <CheckCircle className="w-4 h-4" />
                        Completed
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalModuleDetail;
